import { CollapsiblePanel } from './_barrel.js';


        // Step-by-step guide shown on the Exposure tab: what adtech exposure is,
        // and exactly what to click, in order, to run an assessment for a subject.
        function AeaHowTo({ defaultOpen = false }) {
            const step = (n, title, body) => (
                <div className="flex gap-4 mb-5">
                    <div className="font-display flex items-center justify-center"
                         style={{ minWidth: 34, height: 34, borderRadius: '50%', border: '1px solid var(--gold-dim)', color: 'var(--gold)', fontSize: 16 }}>{n}</div>
                    <div className="flex-1">
                        <div className="text-sm mb-1" style={{ color: 'var(--text)' }}>{title}</div>
                        <div className="text-xs text-muted leading-relaxed" style={{ maxWidth: 640 }}>{body}</div>
                    </div>
                </div>
            );
            return (
                <div className="panel p-5 mb-6">
                    <CollapsiblePanel title="How to run an adtech exposure assessment" defaultOpen={defaultOpen}>
                        <div className="text-sm text-muted mb-5 leading-relaxed" style={{ maxWidth: 680 }}>
                            Data brokers buy and sell mobile <b>location history</b> keyed to a phone's advertising
                            ID (the <b>MAID</b>). This tab lets you find out, <b>with the subject's consent</b>, what
                            those brokers hold — by exercising the subject's legal right to demand it — and then grades
                            how exposed they are (inferred home, workplace, routine, predictability). There are two ways
                            in:
                        </div>

                        <div className="mb-5 pl-1">
                            <div className="font-mono text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--cyan)' }}>Fast path — trial on the subject's own data</div>
                            <div className="text-xs text-muted leading-relaxed" style={{ maxWidth: 680 }}>
                                If the subject can export their own history (Google&nbsp;Timeline / Takeout
                                <span className="font-mono"> Records.json</span>, a fitness <span className="font-mono">.gpx</span>,
                                or geotagged photos), use <b>“Upload location export”</b>. No advertising ID and no waiting
                                for brokers — you get a grade in seconds. Best for a demo or a quick baseline.
                            </div>
                        </div>

                        <div className="mb-4 pl-1">
                            <div className="font-mono text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--gold)' }}>Full path — the real broker data (DSAR)</div>
                        </div>

                        {step('1', 'Record consent',
                            <>Get the subject to sign a short authority letting you act on their behalf, then click
                            <b> “Register device”</b> and upload that signed form. It is stored as tamper-evident
                            evidence (SHA-256) and is the lawful basis for everything that follows. Choose the scope:
                            <i> access only</i>, or <i>access + erasure</i> (recommended — lets you also demand deletion).
                            <br /><span style={{ color: 'var(--green)' }}>Tip:</span> if the client ticked the
                            <b> data-broker authority</b> box during onboarding, this is already on file — “Register
                            device” skips straight to the advertising ID, no upload needed.</>)}

                        {step('2', 'Add the device by advertising ID (MAID)',
                            <>In the same dialog, paste the subject's <b>MAID</b> and pick iOS or Android. The MAID is
                            never stored — we hash it immediately and keep only the hash. To find it on the subject's
                            phone: <b>iPhone</b> Settings → Privacy &amp; Security → Tracking (and Apple's IDFA);
                            <b> Android</b> Settings → Google → Ads → “Your advertising ID”. If ad tracking was reset or
                            off, brokers may still hold <i>historical</i> data — proceed anyway.</>)}

                        {step('3', 'Send DSAR letters to the brokers',
                            <>Click <b>“Log DSAR request”</b> on the device. Pick a broker, choose the request type
                            (Access / Erasure / Objection), and <b>copy the pre-written letter</b> — email it to the
                            broker's privacy contact with the signed authority attached. Log one request per broker you
                            contact; the tab tracks the one-month legal deadline for each.</>)}

                        {step('4', 'Upload each broker response',
                            <>When a broker returns data (CSV/JSON of location points), click <b>“Upload response”</b>
                            on that request. We store the file as evidence, ingest the points through the consent gate,
                            and re-run the analysis automatically. The grade, map and inferred places update as data
                            arrives.</>)}

                        {step('5', 'Read the result & act',
                            <>The <b>grade</b> and <b>“Why this grade”</b> explain the exposure. Confirm or correct the
                            inferred <b>home / work</b> places (that feeds the report). If the subject wants the data
                            gone, use an <i>Erasure</i> request and record the broker's confirmation.</>)}

                        <div className="mt-5 px-4 py-3 text-xs leading-relaxed"
                             style={{ background: 'var(--amber-bg)', border: '1px solid var(--amber)', borderRadius: 'var(--radius-sm)', color: 'var(--text-soft)', maxWidth: 700 }}>
                            <b style={{ color: 'var(--amber)' }}>Before you start.</b> Only run this with the subject's
                            informed, written consent. DSARs made on someone's behalf need their authority attached.
                            Have your data-protection lead sign off the consent wording and retention once — then this
                            flow is repeatable for every client.
                        </div>
                    </CollapsiblePanel>
                </div>
            );
        }

export { AeaHowTo };
